import { Clock } from "lucide-react";
import { formatZonedDateTime } from "../lib/time";
import {
  hasSchedulerVotingDeadline,
  isSchedulerVotingOpen,
  resolveSchedulerVotingDeadline,
} from "../lib/voting-deadlines";

export function SessionVotingDeadlineStatus({
  scheduler,
  now,
  displayTimeZone = null,
  showTimeZone = true,
  compact = false,
}) {
  if (String(scheduler?.status || "OPEN").toUpperCase() !== "OPEN") return null;
  if (!hasSchedulerVotingDeadline(scheduler)) return null;

  const deadline = resolveSchedulerVotingDeadline(scheduler);
  const isOpen = isSchedulerVotingOpen(scheduler, now);
  const deadlineLabel = deadline
    ? formatZonedDateTime(deadline, displayTimeZone, undefined, { showTimeZone })
    : null;

  if (compact) {
    return (
      <span
        data-testid="session-voting-deadline-status"
        className={
          isOpen
            ? "inline-flex items-center gap-1 text-xs font-medium text-slate-500 dark:text-slate-400"
            : "inline-flex items-center gap-1 text-xs font-semibold text-amber-700 dark:text-amber-300"
        }
      >
        <Clock aria-hidden="true" className="h-3.5 w-3.5" />
        {isOpen && deadlineLabel ? `Voting closes ${deadlineLabel}` : "Voting closed"}
      </span>
    );
  }

  return (
    <div
      data-testid="session-voting-deadline-status"
      role={isOpen ? "status" : "alert"}
      className={
        isOpen
          ? "mt-4 flex items-start gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200"
          : "mt-4 flex items-start gap-2 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
      }
    >
      <Clock aria-hidden="true" className="mt-0.5 h-4 w-4 flex-none" />
      <div>
        <p className="font-semibold">{isOpen ? "Voting deadline" : "Voting closed"}</p>
        <p className="mt-0.5 text-xs">
          {isOpen && deadlineLabel
            ? `Submit or edit your votes before ${deadlineLabel}.`
            : deadlineLabel
              ? `The voting deadline passed at ${deadlineLabel}. Existing votes remain visible.`
              : "This poll's voting deadline is invalid. Ask the creator to edit or clear it."}
        </p>
      </div>
    </div>
  );
}
